<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Coins, MapPin, Pause, Square, UserRound } from 'lucide-vue-next'
import { widgetCopy } from '@/i18n/widget-copy'
import { getStreamMessages } from '@/i18n/stream'
import { enabledBuffCount } from '@/buffs/types'
import { reorderBuffOverlay } from '@/buffs/overlay-source'
import { dailyPanelLayoutContent, fitDailyPanel, type DailyPanelProps } from '@/ui/daily-panel'
import type { DailyPanelSkin } from '@/ui/pip-skins'
import { orderCopy } from '@/ui/order-widget'
import WidgetPlayerIdentity from '../WidgetPlayerIdentity.vue'
import WidgetElapsed from '../WidgetElapsed.vue'
import BuffStrip from '../buffs/BuffStrip.vue'
import '@/ui/widget-player.css'
import '@/ui/widget-elapsed.css'
import '@/ui/daily-panel.css'
const props = defineProps<DailyPanelProps & { skin: DailyPanelSkin }>()
// Use the unpadded pixel original instead of the smoothed PWA icon.
const appLogo = new URL(`${import.meta.env.BASE_URL}favicon.png`, window.location.href).href
const root = ref<HTMLElement>()
const copy = computed(() => getStreamMessages(props.locale, props.data.rateUnit))
const local = computed(() => orderCopy[props.locale])
const hasBuffs = computed(() => enabledBuffCount(props.buffs) > 0)
let observer: ResizeObserver | undefined
let frame: number | undefined
let disposed = false
let fontRevision = 0
const layoutContent = computed(() => dailyPanelLayoutContent(props))
function fit() { if (root.value) fitDailyPanel(root.value, `${fontRevision}:${layoutContent.value}`) }
function schedule() {
  if (disposed || frame !== undefined) return
  const win = root.value?.ownerDocument.defaultView
  if (!win?.requestAnimationFrame) { fit(); return }
  frame = win.requestAnimationFrame(() => { frame = undefined; fit() })
}
watch(layoutContent, schedule, { flush: 'post' })
function fontsChanged() { fontRevision++; schedule() }
onMounted(() => {
  const doc = root.value!.ownerDocument
  if (typeof ResizeObserver !== 'undefined') { observer = new ResizeObserver(schedule); observer.observe(root.value!) }
  doc.defaultView?.addEventListener('resize', schedule)
  doc.fonts?.addEventListener('loadingdone', fontsChanged)
  void doc.fonts?.ready.then(fontsChanged)
  fit()
})
onBeforeUnmount(() => {
  disposed = true
  observer?.disconnect()
  const doc = root.value?.ownerDocument
  doc?.defaultView?.removeEventListener('resize', schedule)
  doc?.fonts?.removeEventListener('loadingdone', fontsChanged)
  if (frame !== undefined) doc?.defaultView?.cancelAnimationFrame(frame)
})
</script>
<template>
  <main ref="root" class="daily-panel" :class="{ 'order-widget': skin === 'daily-order' }" :inert="preview || undefined" :data-skin="skin" :data-theme="theme" :data-session="data.status" data-detail="8" :aria-label="widgetCopy(locale, 'Maple Log', '枫记')">
    <header class="daily-header">
      <div class="daily-identity"><img class="daily-logo" :src="appLogo" :alt="widgetCopy(locale, 'Maple Log', '枫记')" width="36" height="36" draggable="false" /><div class="daily-player-copy"><span v-if="!data.level || data.level === '--'" class="daily-player-pending" tabindex="0" :aria-label="widgetCopy(locale, 'Waiting for character', '等待角色识别')" :title="widgetCopy(locale, 'Waiting for character', '等待角色识别')"><UserRound :stroke-width="1.75" aria-hidden="true" /><span>{{ widgetCopy(locale, 'Waiting for character', '等待角色识别') }}</span></span><WidgetPlayerIdentity v-else :level="data.level" :job-name="data.jobName" /><div v-if="data.enabled.mapName" class="daily-map order-map" :title="data.map" tabindex="0"><MapPin aria-hidden="true" /><span>{{ data.map }}</span></div></div></div>
      <span class="daily-status" role="status" :title="`枫记 · ${data.statusText}`" data-panel-measure><span class="daily-status-brand">枫记</span><span class="daily-status-state"><i aria-hidden="true" />{{ data.statusText }}</span></span>
    </header>
    <div class="daily-main">
      <section class="daily-exp" :aria-label="copy.exp"><slot /></section>
      <div v-if="data.enabled.mesos || data.enabled.hp || data.enabled.mp" class="daily-secondary order-secondary">
        <section v-if="data.enabled.mesos" class="daily-mesos" :aria-label="copy.mesos">
          <div class="daily-money-row"><span class="daily-label" data-panel-measure><Coins class="daily-mesos-icon" :stroke-width="1.75" aria-hidden="true" /><span data-panel-measure>{{ copy.mesos }}</span></span><strong data-mesos-rate data-panel-measure>{{ data.mesosRate }}</strong></div>
          <div class="daily-net"><span data-panel-measure>{{ copy.net }}</span><b data-panel-measure>{{ data.mesosNet }}</b></div>
          <div class="daily-accounts"><span>{{ copy.start }} <b data-panel-measure>{{ data.mesosStart }}</b></span><span>{{ copy.balance }} <b data-panel-measure>{{ data.mesosBalanceChange }}</b></span></div>
        </section>
        <section v-if="data.enabled.hp || data.enabled.mp" class="daily-resources" :class="{ 'is-single': data.enabled.hp !== data.enabled.mp }">
          <div v-if="data.enabled.hp" class="daily-resource daily-hp"><div><span>HP</span><b data-panel-measure>{{ data.hpPercent }}</b></div><div class="daily-track"><i :style="{ transform: `scaleX(${data.hpRatio})` }" /></div></div>
          <div v-if="data.enabled.mp" class="daily-resource daily-mp"><div><span>MP</span><b data-panel-measure>{{ data.mpPercent }}</b></div><div class="daily-track"><i :style="{ transform: `scaleX(${data.mpRatio})` }" /></div></div>
        </section>
      </div>
    </div>
    <div v-if="hasBuffs && buffs" class="daily-buffs order-buffs" data-buff-anchor><span class="daily-buff-label">BUFF</span><BuffStrip :snapshot="buffs" compact :icon-size="28" :reorder="preview ? undefined : reorderBuffOverlay" @extra-height="schedule" @error="schedule" /></div>
    <footer class="daily-footer">
      <WidgetElapsed compact :elapsed="data.elapsed" :label="copy.elapsed" />
      <div class="daily-controls"><slot name="actions"><div class="widget-actions"><button type="button" class="action-pause" :aria-label="local.pause" :title="local.pause" tabindex="-1"><Pause aria-hidden="true" /><span>{{ local.pause }}</span></button><button type="button" class="danger" :aria-label="local.end" :title="local.end" tabindex="-1"><Square aria-hidden="true" /><span>{{ local.end }}</span></button></div></slot></div>
    </footer>
  </main>
</template>
